import { HttpClient, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Namespace } from 'enums-lib';
import { ENVIRONMENT, RUNTIME_ENVIRONMENT } from 'environments-lib';
import { IEnvironment, IRuntimeEnvironment, RequestConfigProperties } from 'interfaces-lib';
import { jwtDecode } from 'jwt-decode';
import { IdentityStorage } from 'storage-service-lib';
import { AbstractAuthService } from './auth.service';
import { currentUser } from './current-user';

@Injectable({ providedIn: 'root' })
export class InveniasAuthService extends AbstractAuthService {
  private static readonly INVENIAS_URL_REGEX = new RegExp(/https:\/\/[^.]+\.invenias/); // will also match inveniasnextgen

  constructor(
    @Inject(ENVIRONMENT) protected environment: Partial<IEnvironment>,
    @Inject(RUNTIME_ENVIRONMENT) protected runtimeEnvironment: IRuntimeEnvironment | undefined,
    identityStorage: IdentityStorage,
    http: HttpClient,
  ) {
    super(identityStorage, http);
    console.log('InveniasAuthService created.');
  }

  getRequestProperties(req: HttpRequest<any>, token: string): RequestConfigProperties | undefined {
    if (req.url.match(InveniasAuthService.INVENIAS_URL_REGEX)) {
      return { setHeaders: { Authorization: 'bearer ' + token } };
    }

    return undefined;
  }

  protected getNamespace(): Namespace {
    return Namespace.invenias;
  }

  protected getRequestAuthenticationHostUrl(): string {
    const callbackBase = this.runtimeEnvironment?.runningAsChromeExtension
      ? chrome.identity.getRedirectURL('callback')
      : `${this.environment.uiHost}/callback`;
    const callbackUrl = `${callbackBase}?instance=${this.instanceId}`;
    return `${this.environment.kpHost}/auth/invenias/host?targetCallback=${encodeURIComponent(callbackUrl)}&tenantName=${this.getTenantName()}`;
  }

  protected override redirectToAuthProvider(authUrl: string): void {
    if (this.runtimeEnvironment?.runningAsChromeExtension) {
      this.launchInteractiveAuthFlow(authUrl);
    } else {
      super.redirectToAuthProvider(authUrl);
    }
  }

  private launchInteractiveAuthFlow(authUrl: string): void {
    chrome.identity.launchWebAuthFlow({ url: authUrl, interactive: true }, (responseUrl) => {
      if (chrome.runtime.lastError || !responseUrl) {
        console.error('Chrome identity auth flow failed:', chrome.runtime.lastError?.message);
        return;
      }
      this.finishAuthFlow(responseUrl);
      const token = this.identityStorage.get()?.token;
      if (token) {
        this.resolveWithToken(token);
      }
    });
  }

  private getTenantName(): string {
    const tenant = this.identityStorage.get()?.tenant;
    if (!tenant) {
      throw Error('Tenant undefined during authentication.');
    }
    return tenant;
  }

  protected override postTokenAction(jwt: string): void {
    this.setCurrentUser(jwt);
  }

  private setCurrentUser(jwt: string): void {
    const decoded = jwtDecode<any>(jwt);
    const userIdKey = 'http://www.invenias.com/claims/UserId'; // NOTE: could be PersonId
    currentUser.userInfo = {
      id: decoded[userIdKey],
      name: decoded.name,
      email: decoded.email,
    };
  }
}
